// utils/errorMessages.js
//
// Turns whatever went wrong into ONE plain sentence for the user.
//
// The rule for this whole module: the user never sees a code, a stack, an HTTP
// status, or a browser's internal wording. A client on a bad connection was once
// shown "TypeError: FetchEvent.respondWith received an error: TypeError: Load
// failed" — that is what this exists to prevent. The raw detail still matters,
// but it goes to the error log, not the screen. See ERROR_HANDLING_SPEC.md §5.
//
// Pure functions only: no fetch, no toast, no storage. That keeps the wording
// exhaustively testable, which matters because these strings are the entire
// user-facing surface of every failure in the portal.

/** C# LoginErrorCodes (FireSyncPortal.Models/LoginErrorCodes.cs). */
export const ERROR_CODES = {
    NoError: 0,
    InvalidUserNamePw: 1,
    InvalidRequest: 2,
    Blocked: 3,
    AccountStatus: 4,
    ServerError: 5,
    Unauthorized: 6,
    InvalidLogin: 7,
    NotOrgMember: 8,
    NotFound: 9,
};

/**
 * How a failure is classified. Drives both the wording and — per spec §10 —
 * whether it gets reported to the error log at all.
 */
export const CATEGORY = {
    /** The request never reached the API: offline, DNS, TLS, timeout. */
    TRANSPORT: 'Transport',
    /** The API answered, with success:false and usually a code. */
    APPLICATION: 'Application',
    /** Something threw inside the browser: render error, bad data, TypeError. */
    CLIENT: 'Client',
    UNKNOWN: 'Unknown',
};

export const MESSAGES = {
    OFFLINE: "Can't reach the server. Check your connection and try again.",
    TIMEOUT: 'The server is taking too long to respond. Please try again in a moment.',
    SERVER: 'Something went wrong on our end. The problem has been reported.',
    SESSION_ENDED: 'Your session has ended. Please sign in again.',
    FORBIDDEN: "You don't have permission to do that.",
    NOT_ORG_MEMBER: "You're not a member of this organization.",
    NOT_FOUND: 'That item no longer exists. It may have been deleted.',
    BAD_REQUEST: "Something's missing from that request. Please check the form and try again.",
    SAVE_FAILED: "That change couldn't be saved. Please try again.",
    BAD_CREDENTIALS: "That username or password isn't right.",
    ACCOUNT_PROBLEM: 'There’s a problem with your account. Please contact your administrator.',
    UNKNOWN: 'Something went wrong. The problem has been reported.',
};

// ── Transport detection ─────────────────────────────────────────────────────
// `fetch` gives almost nothing to work with when the network fails: a TypeError
// whose text differs per browser. These are the real strings seen in the wild.
//   Chrome   "Failed to fetch"
//   Safari   "Load failed" / "FetchEvent.respondWith received an error"
//   Firefox  "NetworkError when attempting to fetch resource."
const TRANSPORT_PATTERNS = [
    /failed to fetch/i,
    /load failed/i,
    /networkerror/i,
    /fetchevent\.respondwith/i,
    /network request failed/i,
    /err_internet_disconnected/i,
    /the internet connection appears to be offline/i,
];

/** Does this look like "the request never got there"? */
export function isTransportError(err) {
    if (!err) return false;
    const text = `${err.name || ''} ${err.message || ''}`;
    return TRANSPORT_PATTERNS.some(re => re.test(text));
}

// ── Server message families ─────────────────────────────────────────────────
// The two APIs return 539 distinct ErrorMessage strings, but they collapse into
// a handful of shapes (spec §3). Matching the shape rather than the exact string
// means new messages are handled without a code change — important, because
// nobody is going to update a lookup table of 539 entries.
//
// Order matters: the first match wins, so the specific patterns come first.
const MESSAGE_FAMILIES = [
    { re: /please login|invalid login|invalid authentication key/i, message: MESSAGES.SESSION_ENDED },
    { re: /not authoriz|unauthorized|not permitted|access denied/i, message: MESSAGES.FORBIDDEN },
    { re: /not a member|not an org member/i, message: MESSAGES.NOT_ORG_MEMBER },
    { re: /not found|does not exist|no longer exists/i, message: MESSAGES.NOT_FOUND },
    { re: /is required|missing|invalid request/i, message: MESSAGES.BAD_REQUEST },
    { re: /unable to (update|delete|insert|save)|error (deleting|saving|updating)/i, message: MESSAGES.SAVE_FAILED },
    { re: /invalid user ?name|invalid password|incorrect password/i, message: MESSAGES.BAD_CREDENTIALS },
    { re: /blocked|account status|account is/i, message: MESSAGES.ACCOUNT_PROBLEM },
    { re: /unknown server error/i, message: MESSAGES.SERVER },
];

const CODE_MESSAGES = {
    [ERROR_CODES.InvalidUserNamePw]: MESSAGES.BAD_CREDENTIALS,
    [ERROR_CODES.InvalidRequest]: MESSAGES.BAD_REQUEST,
    [ERROR_CODES.Blocked]: MESSAGES.ACCOUNT_PROBLEM,
    [ERROR_CODES.AccountStatus]: MESSAGES.ACCOUNT_PROBLEM,
    [ERROR_CODES.ServerError]: MESSAGES.SERVER,
    [ERROR_CODES.Unauthorized]: MESSAGES.FORBIDDEN,
    [ERROR_CODES.InvalidLogin]: MESSAGES.SESSION_ENDED,
    [ERROR_CODES.NotOrgMember]: MESSAGES.NOT_ORG_MEMBER,
    [ERROR_CODES.NotFound]: MESSAGES.NOT_FOUND,
};

// A few server messages are written FOR the user, carry a specific detail we
// cannot reproduce (a limit, a size), and are better than anything generic —
// "Too many shortcuts. The maximum is 20." tells the user exactly what to do.
//
// These pass through VERBATIM, but only by explicit pattern. A heuristic was
// tried first and abandoned: "Flux capacitor desynchronised" is structurally
// identical to a real validation message — same length, same plain words, no
// technical markers — so no rule can separate "written for users" from
// "internal text that happens to read like a sentence". Guessing wrong puts
// server internals on screen, which is the thing this module exists to prevent.
//
// Adding to this list is a deliberate act: read the message, confirm the server
// means it for a user, then add it. Anything not listed gets mapped wording.
const PASS_THROUGH_PATTERNS = [
    /^Too many [a-z ]+\. The maximum is \d+\.?$/i,
    /^File size cannot exceed [\d.]+ ?[KMG]B$/i,
    /^Please select at least one [a-z ]+$/i,
    /^Please enter (or generate )?a [a-z ]+$/i,
];

/** Is this one of the curated messages we show verbatim? */
export function isUserSafeMessage(text) {
    if (!text) return false;
    const s = String(text).trim();
    return PASS_THROUGH_PATTERNS.some(re => re.test(s));
}

/** Message for an HTTP status, when there is a response but no usable body. */
export function messageForStatus(status) {
    if (!status) return null;
    if (status === 401 || status === 403) return MESSAGES.FORBIDDEN;
    if (status === 404) return MESSAGES.NOT_FOUND;
    if (status === 408 || status === 504 || status === 502 || status === 503) return MESSAGES.TIMEOUT;
    if (status === 413) return 'That file is too large to upload.';
    if (status >= 500) return MESSAGES.SERVER;
    if (status >= 400) return MESSAGES.BAD_REQUEST;
    return null;
}

/**
 * The one entry point. Give it whatever you have; get back a sentence.
 *
 * @param {object} input
 * @param {Error}  [input.error]        a thrown error, if any
 * @param {number} [input.errorCode]    LoginErrorCodes from the API body
 * @param {string} [input.rawMessage]   ErrorMessage from the API body
 * @param {number} [input.httpStatus]
 * @param {boolean}[input.online]       navigator.onLine at the time
 * @returns {string} always a non-empty, human-readable sentence
 */
export function userMessageFor({ error, errorCode, rawMessage, httpStatus, online } = {}) {
    // Offline beats everything: if the device knows it has no connection, that is
    // the most useful thing we can tell the user, whatever else came back.
    if (online === false) return MESSAGES.OFFLINE;

    if (isTransportError(error)) return MESSAGES.OFFLINE;

    // A real code from the API is the most reliable signal available.
    if (typeof errorCode === 'number' && errorCode !== ERROR_CODES.NoError) {
        const byCode = CODE_MESSAGES[errorCode];
        if (byCode) return byCode;
    }

    // Otherwise fall back to the shape of the server's message.
    if (rawMessage) {
        const family = MESSAGE_FAMILIES.find(f => f.re.test(rawMessage));
        if (family) return family.message;

        // No family matched, but the server may have written something specific
        // and genuinely useful. Prefer it over a generic sentence.
        if (isUserSafeMessage(rawMessage)) return String(rawMessage).trim();
    }

    const byStatus = messageForStatus(httpStatus);
    if (byStatus) return byStatus;

    return MESSAGES.UNKNOWN;
}

/** Classify a failure, for reporting and for deciding whether to report at all. */
export function categorize({ error, errorCode, httpStatus, online } = {}) {
    if (online === false || isTransportError(error)) return CATEGORY.TRANSPORT;
    if (typeof errorCode === 'number' && errorCode !== ERROR_CODES.NoError) return CATEGORY.APPLICATION;
    if (httpStatus) return CATEGORY.APPLICATION;
    if (error) return CATEGORY.CLIENT;
    return CATEGORY.UNKNOWN;
}

/**
 * Should this be sent to the error log? (spec §10.5)
 *
 * Deliberately quiet: "not authorized" alone accounts for 636 occurrences across
 * the APIs, and a permission denial is the system working correctly, not a
 * defect. Reporting those would bury the failures that matter. Starting quiet
 * and adding later is far easier than filtering a table already drowning.
 */
export function shouldReport({ category, errorCode } = {}) {
    if (errorCode === ERROR_CODES.Unauthorized) return false;
    if (errorCode === ERROR_CODES.InvalidRequest) return false;
    if (errorCode === ERROR_CODES.InvalidLogin) return false;
    if (errorCode === ERROR_CODES.InvalidUserNamePw) return false;
    if (errorCode === ERROR_CODES.NotFound) return false;
    return category !== CATEGORY.APPLICATION || errorCode === ERROR_CODES.ServerError;
}

/** True when the failure means the user must sign in again. */
export function isSessionEnded({ errorCode, rawMessage } = {}) {
    if (errorCode === ERROR_CODES.InvalidLogin) return true;
    return !!rawMessage && /please login|invalid login|invalid authentication key/i.test(rawMessage);
}
